import { getRouteNewVersion, getRouteProjectDetails, getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { ReactNode, useState, useEffect } from "react";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { DuplicateProjectModal } from "./DuplicateProjectModal";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";
import { useQueryClient } from "@tanstack/react-query";
import { toast as dsToast } from "@libs/zapi-ui/ds/components/toast";
import { GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";
import { ProjectVersionMenu } from "@/zapi-frontend/pages/ProjectsPage/ui/ProjectVersionMenu/ProjectVersionMenu";
import { ProjectBreadcrumbHeader } from "./_ProjectBreadcrumbHeader";
import { ConfirmDialog } from "@/zapi-frontend/shared/ui/ConfirmDialog/ConfirmDialog";

export function ProjectDetailsHeader({ end }: { end?: ReactNode }) {
  const navigate = useNavigate();
  const { t } = useTranslation(["projectHome", "common"]);
  const { id: detailsId, projectId: versionSetupId } = useParams();
  const id = detailsId ?? versionSetupId;
  // /projects/new-version/:projectId and /projects/review-relaunch/:projectId configure a version that has no
  // document or exports yet, and nothing on those pages loads the project into the store.
  const isVersionSetup = !detailsId && !!versionSetupId;
  const [searchParams, setSearchParams] = useSearchParams();
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const { selectedProject, duplicateProject, fetchSelectedProject } = useProjectStore();
  const { isSystemAdmin, /* availableOrganizations, */ loadOrganizations, switchOrganization } = useOrganizationStore();
  const { setViewMode } = useChapterStore();
  const { closeHandler } = useProjectSidebarStore();
  const isProjectGenerating = useProjectSetupStore(
    (state) => !!state.pollingProjects[id ?? ""] || !!state.activeSetupTasks[id ?? ""]
  );

  const queryClient = useQueryClient();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  useEffect(() => {
    if (isSystemAdmin) loadOrganizations();
  }, [isSystemAdmin, loadOrganizations]);

  useEffect(() => {
    if (!isVersionSetup || !id || selectedProject?.id === id) return;
    void fetchSelectedProject(id);
  }, [isVersionSetup, id, selectedProject?.id, fetchSelectedProject]);

  useEffect(() => {
    if (isVersionSetup) return;
    if (!searchParams.get("tab")) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("tab", "overview");
          return params;
        },
        { replace: true }
      );
    }
  }, [isVersionSetup, searchParams, setSearchParams]);

  const handleProjectNameClick = () => {
    setViewMode("projectOverview");
    closeHandler();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", "overview");
      params.delete("chapterId");
      params.delete("view");
      return params;
    });
  };

  const goToVersion = (versionId: string) => {
    const target = selectedProject?.versions?.find((v) => v.id === versionId);
    navigate(target?.isDraft ? getRouteNewVersion(versionId) : getRouteProjectDetails(versionId));
    if (!target?.name) return;
    dsToast.add({ title: t("header.switchedTo", { name: displayVersionName(target.name) }) });
  };

  const handleSelectVersion = (versionId: string) => {
    if (versionId === selectedProject?.id) return;
    goToVersion(versionId);
  };

  const handleCurrentVersionRemoved = (nextVersionId: string | null) => {
    if (!nextVersionId) {
      navigate(getRouteProjects());
      return;
    }
    goToVersion(nextVersionId);
  };

  // const currentOrganization = availableOrganizations.find(
  //   (org: Organization) => org.id === selectedProject?.organizationId
  // );
  // const currentOrgName = currentOrganization?.name;

  const handleDuplicateProject = async (organizationId?: number) => {
    if (!selectedProject) return;
    const [, error] = await tryCatch<void, AxiosError<{ message: string }>>(
      duplicateProject(selectedProject.id, organizationId)
    );
    if (error) {
      toast.error(error.response?.data.message || t("header.duplicateFailed"));
      return;
    }

    if (isSystemAdmin && organizationId) switchOrganization(organizationId);

    toast.success(t("header.duplicateSuccess"));
    navigate(getRouteProjects());
  };

  const project = selectedProject?.id === id ? selectedProject : null;

  const modals = isSystemAdmin ? (
    <DuplicateProjectModal
      isOpen={isDuplicateModalOpen}
      onClose={() => setIsDuplicateModalOpen(false)}
      onConfirm={handleDuplicateProject}
      currentOrganizationId={selectedProject?.organizationId}
    />
  ) : (
    <ConfirmDialog
      isOpen={isDuplicateModalOpen}
      onClose={() => setIsDuplicateModalOpen(false)}
      onConfirm={() => handleDuplicateProject()}
      title={t("projectHome:header.duplicateProject")}
      description={t("projectHome:header.duplicateConfirm")}
      confirmText={t("projectHome:header.duplicateAction")}
      cancelText={t("common:cancel")}
    />
  );

  const versionMenu = project && project.organizationId != null && (
    <ProjectVersionMenu
      projectId={project.id}
      organizationId={project.organizationId}
      currentId={project.id}
      versionGroupId={project.versionGroupId}
      versions={
        project.versions?.length
          ? project.versions
          : [{ id: project.id, name: project.versionDisplayName ?? "", date: project.lastModified ?? "" }]
      }
      hasEdit={hasEdit}
      appearance="plain"
      className="max-w-36 shrink sm:max-w-60"
      onSelect={handleSelectVersion}
      onChanged={() => {
        void fetchSelectedProject(project.id, true);
        void queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY] });
        if (isVersionSetup) void queryClient.invalidateQueries({ queryKey: ["project-new-version"] });
      }}
      onCurrentRemoved={handleCurrentVersionRemoved}
      onCurrentRestored={(versionId) => navigate(getRouteProjectDetails(versionId))}
    />
  );

  return (
    <>
      <ProjectBreadcrumbHeader
        project={project}
        versionMenu={versionMenu}
        exportDisabled={isProjectGenerating}
        showExport={!isVersionSetup}
        end={end}
        onProjectNameClick={isVersionSetup ? undefined : handleProjectNameClick}
      />
      {modals}
    </>
  );
}
